import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminCommissionService } from '../../../services/adminCommissionService';
import { Plus, Edit, Trash2, Save, X, Percent, DollarSign } from 'lucide-react';
import { Button } from '../../shared';

const CommissionSettings = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    commission_rate: '',
    commission_type: 'percentage',
    fixed_amount: '',
    description: '',
    is_active: true,
    is_default: false,
  });

  // Fetch commission settings
  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['adminCommissionSettings'],
    queryFn: () => adminCommissionService.getAll(),
  });

  const settings = settingsResponse?.data || [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => adminCommissionService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminCommissionSettings']);
      setIsModalOpen(false);
      resetForm();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminCommissionService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminCommissionSettings']);
      setIsModalOpen(false);
      setEditingSetting(null);
      resetForm();
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => adminCommissionService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminCommissionSettings']);
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      commission_rate: '',
      commission_type: 'percentage',
      fixed_amount: '',
      description: '',
      is_active: true,
      is_default: false,
    });
  };

  const handleOpenModal = (setting = null) => {
    if (setting) {
      setEditingSetting(setting);
      setFormData({
        name: setting.name,
        commission_rate: setting.commission_rate,
        commission_type: setting.commission_type,
        fixed_amount: setting.fixed_amount || '',
        description: setting.description || '',
        is_active: setting.is_active,
        is_default: setting.is_default,
      });
    } else {
      setEditingSetting(null);
      resetForm();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSetting(null);
    resetForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      commission_rate: parseFloat(formData.commission_rate),
      fixed_amount: formData.commission_type === 'fixed' ? parseFloat(formData.fixed_amount) : null,
      is_active: formData.is_active,
      is_default: formData.is_default,
    };

    if (editingSetting) {
      updateMutation.mutate({ id: editingSetting.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this commission setting?')) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading commission settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 pt-5">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 text-sm text-emerald-950 mb-4">
          <p className="font-semibold">How commission protects both sides</p>
          <ul className="mt-2 list-disc pl-5 space-y-1 text-emerald-900/90">
            <li>
              Client pays <strong>vendor price + this service charge</strong> (display currency, usually
              USD).
            </li>
            <li>
              Vendor is owed the <strong>full vendor price</strong> (converted at the locked FX rate for
              Stripe / payouts).
            </li>
            <li>
              Stripe processing fees come from the <strong>platform commission only</strong> — never the
              vendor share.
            </li>
            <li>
              Keep the rate high enough to cover Stripe’s % + fixed fee after FX (Settings → Settlement).
            </li>
          </ul>
        </div>
      </div>
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Commission Settings</h2>
          <p className="mt-1 text-sm text-gray-600">
            Configure commission rates applied to booking charges
          </p>
        </div>
        <Button
          variant="primary"
          size="md"
          icon={Plus}
          onClick={() => handleOpenModal()}
        >
          Add Commission Setting
        </Button>
      </div>

      <div className="p-6">
        {settings.length === 0 ? (
          <div className="text-center py-12">
            <Percent className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Commission Settings</h3>
            <p className="text-gray-600 mb-4">Get started by creating your first commission setting.</p>
            <Button
              variant="primary"
              size="md"
              icon={Plus}
              onClick={() => handleOpenModal()}
            >
              Create Commission Setting
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {settings.map((setting) => (
              <div
                key={setting.id}
                className={`p-4 border-2 rounded-lg ${
                  setting.is_default
                    ? 'border-black bg-gray-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {setting.name}
                      </h3>
                      {setting.is_default && (
                        <span className="px-2 py-1 text-xs font-medium bg-black text-white rounded">
                          Default
                        </span>
                      )}
                      {setting.is_active ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                          Inactive
                        </span>
                      )}
                    </div>
                    {setting.description && (
                      <p className="text-sm text-gray-600 mb-3">{setting.description}</p>
                    )}
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        {setting.commission_type === 'percentage' ? (
                          <Percent className="w-4 h-4 text-gray-500" />
                        ) : (
                          <DollarSign className="w-4 h-4 text-gray-500" />
                        )}
                        <span className="text-sm text-gray-700">
                          {setting.commission_type === 'percentage'
                            ? `${setting.commission_rate}%`
                            : `$${parseFloat(setting.fixed_amount).toFixed(2)}`}
                        </span>
                      </div>
                      {setting.created_by && (
                        <span className="text-xs text-gray-500">
                          Created: {new Date(setting.created_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleOpenModal(setting)}
                      className="p-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-md transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    {!setting.is_default && (
                      <button
                        onClick={() => handleDelete(setting.id)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingSetting ? 'Edit Commission Setting' : 'Create Commission Setting'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="e.g., Default Commission, Premium Commission"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Commission Type *
                </label>
                <select
                  required
                  value={formData.commission_type}
                  onChange={(e) => setFormData({ ...formData, commission_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed Amount</option>
                </select>
              </div>

              {formData.commission_type === 'percentage' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Commission Rate (%) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.commission_rate}
                    onChange={(e) => setFormData({ ...formData, commission_rate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                    placeholder="e.g., 10.5"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fixed Amount ($) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.fixed_amount}
                    onChange={(e) => setFormData({ ...formData, fixed_amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                    placeholder="e.g., 50.00"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="Optional description for this commission setting"
                />
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                  />
                  <span className="text-sm text-gray-700">Set as Default</span>
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  icon={Save}
                  loading={createMutation.isPending || updateMutation.isPending}
                >
                  {editingSetting ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionSettings;

